/*
  Keyboard and screen-reader support for the desktop mega menu.

  The <menu-navigation> element (menu-navigation.*.min.js, built from source
  that isn't in the theme) only opens on mouse hover. This adds, on top of it:
  - click / Enter / Space on a top-level button toggles its dropdown
  - aria-expanded kept in sync with the open state (hover included)
  - Tab from an open button moves into its dropdown, and Tab past the last
    link in the dropdown continues to the next top-level item
  - Escape closes the dropdown and returns focus to its button
  - the dropdown closes when focus leaves the menu
*/
(function () {
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

  function init(nav) {
    if (nav.dataset.a11yReady) return;
    const buttons = Array.from(nav.querySelectorAll('button[data-mega-menu-item][data-dropdown]'));
    if (!buttons.length) return;
    nav.dataset.a11yReady = 'true';

    const topItems = Array.from(nav.querySelectorAll('[data-mega-menu-item]'));
    const panelFor = (button) =>
      nav.querySelector('[data-mega-menu-dropdown-item][data-handle="' + button.dataset.handle + '"]');
    const openButton = () => buttons.find((b) => b.classList.contains('is-active'));

    const open = (button) => nav.handleMenuItemOver(button);
    const close = () => {
      nav.cancelReset();
      nav.isAnimating = false;
      nav.resetDropdown();
    };

    // Keep aria-expanded in sync however the menu was opened (hover or keyboard)
    const sync = () => buttons.forEach((b) => b.setAttribute('aria-expanded', b.classList.contains('is-active') ? 'true' : 'false'));
    const observer = new MutationObserver(sync);
    buttons.forEach((b) => observer.observe(b, { attributes: true, attributeFilter: ['class'] }));
    sync();

    buttons.forEach((button) => {
      button.addEventListener('click', (event) => {
        const isOpen = button.classList.contains('is-active');
        // event.detail is 0 for Enter/Space. A mouse click on a menu that hover
        // already opened should leave it open rather than toggle it shut.
        if (isOpen && event.detail === 0) {
          close();
        } else if (!isOpen) {
          open(button);
        }
      });
    });

    nav.addEventListener('keydown', (event) => {
      const active = openButton();
      if (!active) return;
      const panel = panelFor(active);

      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        active.focus();
        return;
      }

      if (event.key !== 'Tab' || !panel) return;
      const links = Array.from(panel.querySelectorAll(FOCUSABLE));
      if (!links.length) return;
      const nextTop = topItems[topItems.indexOf(active) + 1];

      if (!event.shiftKey && document.activeElement === active) {
        // Button -> first link in its dropdown
        event.preventDefault();
        links[0].focus();
      } else if (!event.shiftKey && document.activeElement === links[links.length - 1] && nextTop) {
        // Last dropdown link -> next top-level item
        event.preventDefault();
        close();
        nextTop.focus();
      } else if (event.shiftKey && document.activeElement === links[0]) {
        // First dropdown link -> back to its button
        event.preventDefault();
        active.focus();
      }
    });

    nav.addEventListener('focusout', (event) => {
      if (openButton() && !nav.contains(event.relatedTarget)) close();
    });
  }

  customElements.whenDefined('menu-navigation').then(() => {
    // The element initialises itself on the next animation frame
    requestAnimationFrame(() => requestAnimationFrame(() => {
      document.querySelectorAll('menu-navigation').forEach(init);
    }));
  });
})();
